import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  Building2,
  ArrowLeft,
  QrCode
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../../config/api';

const VolunteerVerification = () => {
  const { id } = useParams();
  const [volunteer, setVolunteer] = useState(null);
  const [ngo, setNgo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyVolunteer = async () => {
      try {
        setLoading(true);
        // This would be a public endpoint for verification
        const response = await api.get(`/volunteers/${id}/verify`);
        
        if (response.data.success) {
          setVolunteer(response.data.data.volunteer);
          setNgo(response.data.data.ngo);
        } else {
          setError('Volunteer not found or verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setError('Failed to verify volunteer');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      verifyVolunteer();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying volunteer...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!volunteer || !ngo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Volunteer Not Found</h1>
          <p className="text-gray-600 mb-6">The volunteer ID could not be verified.</p>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Volunteer Verified</h1>
          <p className="text-gray-600">This volunteer ID card is authentic and valid</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* NGO Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {ngo.logo && (
                  <img 
                    src={ngo.logo} 
                    alt="NGO Logo" 
                    className="w-12 h-12 rounded-full object-cover"
                    onError={(e) => {
                      if (e.target && e.target.style) {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                )}
                <div>
                  <h2 className="text-xl font-bold">{ngo.name}</h2>
                  <p className="text-blue-100">Official Volunteer Verification</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-blue-100">Verification Date</div>
                <div className="font-semibold">{new Date().toLocaleDateString()}</div>
              </div>
            </div>
          </div>

          {/* Volunteer Information */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <User className="mr-2" size={20} />
                  Personal Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Name:</span>
                    <span className="text-gray-800">
                      {volunteer.firstName} {volunteer.lastName}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Status:</span>
                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                      volunteer.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : volunteer.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {volunteer.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Join Date:</span>
                    <span className="text-gray-800">
                      {volunteer.joinDate ? new Date(volunteer.joinDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  {volunteer.dateOfBirth && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-600 w-24">Age:</span>
                      <span className="text-gray-800">
                        {new Date().getFullYear() - new Date(volunteer.dateOfBirth).getFullYear()} years
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Phone className="mr-2" size={20} />
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Phone:</span>
                    <span className="text-gray-800">{volunteer.phone || 'N/A'}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Email:</span>
                    <span className="text-gray-800">{volunteer.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600 w-24">Location:</span>
                    <span className="text-gray-800">
                      {volunteer.city || 'N/A'}, {volunteer.state || 'N/A'}
                    </span>
                  </div>
                  {volunteer.emergencyContact?.phone && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-600 w-24">Emergency:</span>
                      <span className="text-gray-800">{volunteer.emergencyContact.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Skills and Experience */}
            {volunteer.skills && volunteer.skills.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Skills & Experience</h3>
                <div className="flex flex-wrap gap-2">
                  {volunteer.skills.map((skill, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
                {volunteer.experience && (
                  <p className="mt-3 text-gray-600">{volunteer.experience}</p>
                )}
              </div>
            )}

            {/* Activity Stats */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Activity Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{volunteer.totalHours || 0}</div>
                  <div className="text-sm text-gray-600">Total Hours</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{volunteer.rating || 'N/A'}</div>
                  <div className="text-sm text-gray-600">Rating</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {volunteer.projects ? volunteer.projects.length : 0}
                  </div>
                  <div className="text-sm text-gray-600">Projects</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 mb-4">
            This verification confirms the authenticity of the volunteer ID card
          </p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VolunteerVerification;
